import React from "react";

interface DetailRow {
  key: string;
  value: string;
}

function isDetailRow(value: unknown): value is DetailRow {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.key === "string" && typeof obj.value === "string";
}

/**
 * Key-value detail card. Payload shape mirrors the workspace render-spec
 * compiler:
 *   { rows: Array<{ key, value }> }
 *
 * Visual spec: see .kv-row / .kv-key / .kv-value in sandbox-styles.css.
 * Rows are baseline-aligned with bottom-borders (the last row drops it).
 */
export default function DetailBlock({ payload }: { payload: Record<string, unknown> }) {
  const rawRows = Array.isArray(payload.rows) ? payload.rows : [];
  const rows = rawRows.filter(isDetailRow);

  if (rows.length === 0) {
    return (
      <div
        aria-label="detail-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No details to display.
      </div>
    );
  }

  return (
    <div
      aria-label="detail"
      className="rounded-lg p-4"
      style={{
        backgroundColor: "var(--c-surface)",
        border: "1px solid var(--c-border)",
      }}
    >
      {rows.map((row, idx) => {
        const isLast = idx === rows.length - 1;
        return (
          <div
            key={`${row.key}-${idx}`}
            className="flex items-baseline justify-between"
            style={{
              padding: "6px 0",
              borderBottom: isLast ? "none" : "1px solid var(--c-border)",
              fontSize: 13,
            }}
          >
            <span style={{ color: "var(--c-text-secondary)" }}>{row.key}</span>
            <span className="font-medium text-right" style={{ color: "var(--c-text)" }}>
              {row.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}
